import { useEffect, useEffectEvent, useRef } from 'react';

type UseScrollFooterParams = {
  handleRef: React.RefObject<HTMLElement | null>;
  onSwipeUpFromHandle: () => void;
  onSwipeDownOutside: () => void;
  disabled?: boolean;
  threshold?: number;
};

export const useScrollFooter = ({
  handleRef,
  onSwipeUpFromHandle,
  onSwipeDownOutside,
  disabled,
  threshold = 20
}: UseScrollFooterParams) => {
  const startYRef = useRef(0);
  const currentYRef = useRef(0);
  const startedOnHandleRef = useRef(false);

  const handleGesture = useEffectEvent((accumulatedDelta: number) => {
    const isSwipeDown = accumulatedDelta < -threshold;
    const isSwipeUp = accumulatedDelta > threshold;

    if (isSwipeUp && startedOnHandleRef.current) {
      onSwipeUpFromHandle();
      return;
    }

    if (isSwipeDown && !startedOnHandleRef.current) {
      onSwipeDownOutside();
    }
  });

  useEffect(() => {
    if (disabled) {
      return;
    }

    let accumulated = 0;

    const onPointerDown = (event: PointerEvent) => {
      startYRef.current = event.clientY;
      currentYRef.current = event.clientY;

      startedOnHandleRef.current = handleRef.current?.contains(event.target as Node) ?? false;

      accumulated = 0;
    };

    const onPointerMove = (event: PointerEvent) => {
      currentYRef.current = event.clientY;

      const delta = startYRef.current - currentYRef.current;
      accumulated += delta;

      handleGesture(accumulated);

      startYRef.current = currentYRef.current;
    };

    const onPointerUp = (event: PointerEvent) => {
      startedOnHandleRef.current = handleRef.current?.contains(event.target as Node) ?? false;

      accumulated = 0;
    };

    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);

    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [disabled, handleRef, onSwipeUpFromHandle, onSwipeDownOutside]);
};
